import React, { useEffect, useState, useMemo } from "react";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Stack,
  IconButton,
  Typography,
  Chip,
  Checkbox,
  TextField,
  Autocomplete,
  Popper,
  Button as MuiButton,
} from "@mui/material";
import {
  Close as CloseIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
} from "@mui/icons-material";
import { MdOutlineAssignment } from "react-icons/md";
import { Button } from "../../Common";

const AssignForm = ({ handleClose, formik, data = [], modalOpen }) => {
  // Normalize initial groups from modalOpen or formik
  const extractGroupIds = (rawList) => {
    if (!Array.isArray(rawList)) return [];
    return rawList
      .map((g) => (typeof g === "object" && g ? g._id || g.id : g))
      .filter(Boolean);
  };

  const [selectedGroupIds, setSelectedGroupIds] = useState(() => {
    return extractGroupIds(modalOpen?.selectedGroup || formik?.values?.groups || []);
  });

  useEffect(() => {
    const ids = extractGroupIds(modalOpen?.selectedGroup || formik?.values?.groups || []);
    setSelectedGroupIds(ids);
    formik?.setFieldValue("groups", ids);
  }, [modalOpen?.selectedGroup]);

  // Lookup map for fast group finding
  const groupMap = useMemo(() => {
    const map = new Map();
    (data || []).forEach((g) => {
      if (g?._id) map.set(g._id, g);
    });
    return map;
  }, [data]);

  // Selected group objects for Autocomplete
  const selectedGroupObjects = useMemo(() => {
    return selectedGroupIds
      .map((id) => groupMap.get(id))
      .filter(Boolean);
  }, [selectedGroupIds, groupMap]);

  const handleAutocompleteChange = (event, newValues) => {
    const newIds = newValues.map((item) => item._id).filter(Boolean);
    setSelectedGroupIds(newIds);
    formik?.setFieldValue("groups", newIds);
  };

  const handleDeleteChip = (idToRemove) => {
    const updated = selectedGroupIds.filter((id) => id !== idToRemove);
    setSelectedGroupIds(updated);
    formik?.setFieldValue("groups", updated);
  };

  const handleSelectAll = () => {
    const allIds = (data || []).map((g) => g._id).filter(Boolean);
    setSelectedGroupIds(allIds);
    formik?.setFieldValue("groups", allIds);
  };

  const handleClearAll = () => {
    setSelectedGroupIds([]);
    formik?.setFieldValue("groups", []);
  };

  const formTitle = modalOpen?.formTitle || modalOpen?.title;

  return (
    <>
      <DialogTitle
        sx={{
          m: 0,
          p: 2.5,
          pb: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #f0f0f0",
        }}
        id="Common_modal"
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "50%",
              backgroundColor: "rgba(255, 106, 0, 0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#ff6a00",
              flexShrink: 0,
            }}
          >
            <MdOutlineAssignment size={22} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, fontSize: "17px", color: "#111827", lineHeight: 1.3 }}
            >
              Assign Form to Groups
            </Typography>
            {formTitle && (
              <Typography
                variant="body2"
                sx={{
                  color: "#6B7280",
                  fontSize: "13px",
                  mt: 0.3,
                  maxWidth: "360px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                Form: <strong style={{ color: "#374151" }}>{formTitle}</strong>
              </Typography>
            )}
          </Box>
        </Stack>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
            "&:hover": { color: "#111" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, pt: 2 }}>
        <Stack spacing={2}>
          {/* Header row for select controls */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mt: 0.5 }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, fontSize: "13.5px", color: "#374151" }}
            >
              Select Groups ({selectedGroupIds.length} of {data?.length || 0} selected)
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <MuiButton
                size="small"
                onClick={handleSelectAll}
                disabled={!data || data.length === 0 || selectedGroupIds.length === data.length}
                sx={{
                  fontSize: "12px !important",
                  fontWeight: "600 !important",
                  textTransform: "none !important",
                  color: "#ff6a00 !important",
                  border: "1px solid #FED7AA !important",
                  backgroundColor: "#FFF7ED !important",
                  py: "3px !important",
                  px: "10px !important",
                  minWidth: "auto !important",
                  lineHeight: "1.2 !important",
                  borderRadius: "5px !important",
                  boxShadow: "none !important",
                  "&:hover": {
                    backgroundColor: "#FFEDD5 !important",
                    borderColor: "#FDBA74 !important",
                  },
                  "&.Mui-disabled": {
                    color: "#9CA3AF !important",
                    borderColor: "#E5E7EB !important",
                    backgroundColor: "#F3F4F6 !important",
                  },
                }}
              >
                Select All
              </MuiButton>
              <MuiButton
                size="small"
                onClick={handleClearAll}
                disabled={selectedGroupIds.length === 0}
                sx={{
                  fontSize: "12px !important",
                  fontWeight: "500 !important",
                  textTransform: "none !important",
                  color: "#4B5563 !important",
                  border: "1px solid #E5E7EB !important",
                  backgroundColor: "#F9FAFB !important",
                  py: "3px !important",
                  px: "10px !important",
                  minWidth: "auto !important",
                  lineHeight: "1.2 !important",
                  borderRadius: "5px !important",
                  boxShadow: "none !important",
                  "&:hover": {
                    backgroundColor: "#F3F4F6 !important",
                    color: "#DA2921 !important",
                    borderColor: "#FCA5A5 !important",
                  },
                  "&.Mui-disabled": {
                    color: "#9CA3AF !important",
                    borderColor: "#E5E7EB !important",
                    backgroundColor: "#F3F4F6 !important",
                  },
                }}
              >
                Clear All
              </MuiButton>
            </Stack>
          </Stack>

          {/* Searchable Multi-Select Autocomplete */}
          <Autocomplete
            multiple
            id="assign-groups-autocomplete"
            options={data || []}
            disableCloseOnSelect
            value={selectedGroupObjects}
            onChange={handleAutocompleteChange}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            getOptionLabel={(option) => option.groupName || ""}
            noOptionsText="No groups found"
            PopperComponent={(props) => (
              <Popper {...props} sx={{ zIndex: 1400 }} />
            )}
            ListboxProps={{
              style: {
                maxHeight: "260px",
                overflowY: "auto",
              },
            }}
            renderOption={(props, option, { selected }) => {
              const { key, ...otherProps } = props;
              return (
                <li key={key || option._id} {...otherProps}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    style={{ marginRight: 8 }}
                    checked={selected}
                    sx={{
                      color: "#D1D5DB",
                      "&.Mui-checked": {
                        color: "#ff6a00",
                      },
                    }}
                  />
                  <Typography sx={{ fontSize: "13.5px", color: "#374151" }}>
                    {option.groupName}
                  </Typography>
                </li>
              );
            }}
            renderTags={() => null}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={selectedGroupIds.length > 0 ? "Type to search more groups..." : "Search and select groups..."}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: "13.5px",
                    borderRadius: "6px",
                  },
                }}
              />
            )}
          />

          {/* Selected Groups Chips Container */}
          <Box>
            <Typography
              variant="caption"
              sx={{ color: "#6B7280", fontWeight: 500, display: "block", mb: 0.8 }}
            >
              Assigned Groups:
            </Typography>
            <Box
              sx={{
                minHeight: "70px",
                maxHeight: "150px",
                overflowY: "auto",
                p: 1.2,
                borderRadius: "8px",
                backgroundColor: "#F9FAFB",
                border: "1px dashed #D1D5DB",
                display: "flex",
                flexWrap: "wrap",
                gap: 0.8,
                alignContent: "flex-start",
              }}
            >
              {selectedGroupIds.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{ color: "#9CA3AF", fontSize: "13px", fontStyle: "italic", m: "auto" }}
                >
                  No groups selected. Search and select groups above.
                </Typography>
              ) : (
                selectedGroupIds.map((groupId) => {
                  const group = groupMap.get(groupId);
                  const label = group?.groupName || "Unknown Group";
                  return (
                    <Chip
                      key={groupId}
                      label={label}
                      size="small"
                      onDelete={() => handleDeleteChip(groupId)}
                      sx={{
                        fontSize: "12px",
                        fontWeight: 500,
                        backgroundColor: "#FFF5ED",
                        color: "#C2410C",
                        borderColor: "#FED7AA",
                        border: "1px solid",
                        "& .MuiChip-deleteIcon": {
                          color: "#EA580C",
                          "&:hover": { color: "#9A3412" },
                        },
                      }}
                    />
                  );
                })
              )}
            </Box>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          px: 2.5,
          borderTop: "1px solid #f0f0f0",
          justifyContent: "flex-end",
          gap: 1,
        }}
      >
        <Button
          buttonName="Cancel"
          size="small"
          color="white"
          onClick={handleClose}
          style={{ textTransform: "none", minWidth: "85px" }}
        />
        <Button
          buttonName="Assign"
          size="small"
          onClick={formik?.handleSubmit}
          style={{ textTransform: "none", minWidth: "95px" }}
        />
      </DialogActions>
    </>
  );
};

export default AssignForm;
